import { ContractStatus, ParkingStatus, ParkingType, SubscriptionRenewalStatus, Term, NonRenewalReason } from '@parkupp/core'
import { normaliseCity, normaliseCityForSearch, NormalisationResult } from './city-normalisation'
import {
    NationalHeadline, CityRow, SuburbRow, AmenityPremium,
    PriceBandRow, AskingVsAchieved, LeadReasonRow,
    MonthlyUptake, CityMonthlyUptake, TermMixRow, RenewalRow,
    NonRenewalReasonRow, PRICE_BANDS,
} from './market-insights.types'

// ─── Stat helpers ───

function median(values: number[]): number {
    if (values.length === 0) return 0
    const sorted = [...values].sort((a, b) => a - b)
    const mid = Math.floor(sorted.length / 2)
    return sorted.length % 2 !== 0 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2
}

function percentile(values: number[], p: number): number {
    if (values.length === 0) return 0
    const sorted = [...values].sort((a, b) => a - b)
    const idx = (p / 100) * (sorted.length - 1)
    const lo = Math.floor(idx)
    const hi = Math.ceil(idx)
    if (lo === hi) return sorted[lo]
    return sorted[lo] + (sorted[hi] - sorted[lo]) * (idx - lo)
}

function mean(values: number[]): number {
    if (values.length === 0) return 0
    return values.reduce((s, v) => s + v, 0) / values.length
}

function centsToRands(cents: number): number {
    return cents / 100
}

function toDate(ts: any): Date | null {
    if (!ts) return null
    if (ts.toDate) return ts.toDate()
    if (ts.seconds) return new Date(ts.seconds * 1000)
    if (ts instanceof Date) return ts
    const d = new Date(ts)
    return isNaN(d.getTime()) ? null : d
}

// ─── Data prep: attach normalised metro to raw records ───

export interface NormalisedParking {
    raw: any
    metro: string
    resolved: boolean
    monthlyPriceCents: number | null
    suburb: string
    hasCoveredParking: boolean
    hasOnSiteGuard: boolean
    has247Access: boolean
    hasBasementParking: boolean
}

export interface NormalisedSubscription {
    raw: any
    metro: string
    parkingKey: string
    priceCents: number
    baysBooked: number
    period: number
    isActive: boolean
    createdAt: Date | null
}

export interface NormalisedLead {
    raw: any
    metro: string
    reason: string
    createdAt: Date | null
}

export interface NormalisedSearch {
    raw: any
    metro: string
    createdAt: Date | null
}

function getMonthlyPrice(parking: any): number | null {
    if (!parking.plans || !Array.isArray(parking.plans)) return null
    const monthly = parking.plans.find((p: any) => p.term === Term.MONTHLY)
    if (monthly && monthly.feeInCents > 0) return monthly.feeInCents
    return null
}

function hasAmenity(parking: any, key: string): boolean {
    if (!parking.parkingAmenities || !Array.isArray(parking.parkingAmenities)) return false
    return parking.parkingAmenities.some((a: any) => a.key === key && (a.value === true || a.value === 'true'))
}

export function normaliseParkings(rawParkings: any[]): { parkings: NormalisedParking[]; unresolvedCount: number } {
    let unresolvedCount = 0
    const parkings: NormalisedParking[] = []

    for (const p of rawParkings) {
        if (p.parkingStatus !== ParkingStatus.PUBLISHED) continue

        const loc = p.parkingLocation || {}
        const result = normaliseCity(loc.city, loc.latitude, loc.longitude)
        if (!result.resolved) unresolvedCount++

        parkings.push({
            raw: p,
            metro: result.metro,
            resolved: result.resolved,
            monthlyPriceCents: getMonthlyPrice(p),
            suburb: (loc.suburb || '').trim(),
            hasCoveredParking: hasAmenity(p, 'coveredParking'),
            hasOnSiteGuard: hasAmenity(p, 'onSiteGuard'),
            has247Access: hasAmenity(p, 'twentyFourSevenAccess'),
            hasBasementParking: hasAmenity(p, 'basementParking'),
        })
    }

    return { parkings, unresolvedCount }
}

export function normaliseSubscriptions(rawSubs: any[], parkingMetroMap: Map<string, string>): NormalisedSubscription[] {
    const ACTIVE_STATUSES = [ContractStatus.active, ContractStatus.completed, ContractStatus.bouncing, ContractStatus.renewing]
    const result: NormalisedSubscription[] = []

    for (const s of rawSubs) {
        const parkingKey = s.parkingRef?.id || s.parkingRef?.path?.split('/')?.pop() || ''
        const metro = parkingMetroMap.get(parkingKey) || ''
        const priceCents = s.plan?.feeInCents || 0
        const term = s.plan?.term

        if (term !== Term.MONTHLY && term !== 'MONTHLY') continue

        result.push({
            raw: s,
            metro,
            parkingKey,
            priceCents,
            baysBooked: s.baysBooked || 1,
            period: s.period || 1,
            isActive: ACTIVE_STATUSES.includes(s.status),
            createdAt: toDate(s.createdAt),
        })
    }

    return result
}

export function normaliseLeads(rawLeads: any[]): NormalisedLead[] {
    return rawLeads.map((l) => {
        const result = normaliseCity(l.city, l.latitude, l.longitude)
        return {
            raw: l,
            metro: result.metro,
            reason: l.reasonForNotFinding || 'OTHER',
            createdAt: toDate(l.createdAt),
        }
    })
}

export function normaliseSearches(rawSearches: any[]): NormalisedSearch[] {
    return rawSearches.map((s) => {
        const result = normaliseCityForSearch(s.city, s.latitude, s.longitude)
        return {
            raw: s,
            metro: result.metro,
            createdAt: toDate(s.createdAt),
        }
    })
}

// ─── A. National headline ───

export function computeNationalHeadline(
    parkings: NormalisedParking[],
    subs: NormalisedSubscription[]
): NationalHeadline {
    const totalBays = parkings.reduce((s, p) => s + (p.raw.numberOfBays || 0), 0)
    const activeBays = subs.filter((s) => s.isActive).reduce((s, sub) => s + sub.baysBooked, 0)
    const prices = parkings.filter((p) => p.monthlyPriceCents != null).map((p) => p.monthlyPriceCents!)

    const activeSubs = subs.filter((s) => s.isActive)

    return {
        publishedBuildings: parkings.length,
        publishedBays: totalBays,
        activeBays,
        medianPriceRands: centsToRands(median(prices)),
        avgContractMonths: mean(activeSubs.map((s) => s.period)),
        avgBaysPerContract: mean(activeSubs.map((s) => s.baysBooked)),
        totalContractValueRands: centsToRands(activeSubs.reduce((s, sub) => s + sub.priceCents * sub.baysBooked * sub.period, 0)),
    }
}

// ─── B. City comparison ───

export function computeCityRows(
    parkings: NormalisedParking[],
    subs: NormalisedSubscription[],
    searches: NormalisedSearch[],
    leads: NormalisedLead[],
    minSampleSize: number
): CityRow[] {
    const cities = new Set<string>()
    parkings.forEach((p) => cities.add(p.metro))
    subs.forEach((s) => { if (s.metro) cities.add(s.metro) })

    const searchByCity = new Map<string, number>()
    searches.forEach((s) => searchByCity.set(s.metro, (searchByCity.get(s.metro) || 0) + 1))

    const leadByCity = new Map<string, number>()
    leads.forEach((l) => leadByCity.set(l.metro, (leadByCity.get(l.metro) || 0) + 1))

    const rows: CityRow[] = []

    for (const city of cities) {
        const cityParkings = parkings.filter((p) => p.metro === city)
        const citySubs = subs.filter((s) => s.metro === city)
        const activeSubs = citySubs.filter((s) => s.isActive)

        const totalBays = cityParkings.reduce((s, p) => s + (p.raw.numberOfBays || 0), 0)
        const activeBays = activeSubs.reduce((s, sub) => s + sub.baysBooked, 0)
        const prices = cityParkings.filter((p) => p.monthlyPriceCents != null).map((p) => p.monthlyPriceCents!)

        const unoccupiedBays = Math.max(totalBays - activeBays, 0)
        const searchCount = searchByCity.get(city) || 0

        rows.push({
            city,
            buildings: cityParkings.length,
            totalBays,
            activeBays,
            medianPrice: centsToRands(median(prices)),
            meanPrice: centsToRands(mean(prices)),
            p25Price: centsToRands(percentile(prices, 25)),
            p75Price: centsToRands(percentile(prices, 75)),
            avgContractMonths: mean(activeSubs.map((s) => s.period)),
            avgBaysPerContract: mean(activeSubs.map((s) => s.baysBooked)),
            avgContractValue: centsToRands(mean(activeSubs.map((s) => s.priceCents * s.baysBooked * s.period))),
            demandIndex: unoccupiedBays > 0 ? searchCount / unoccupiedBays : searchCount > 0 ? Infinity : 0,
            sampleSizeParkings: cityParkings.length,
            sampleSizeSubscriptions: activeSubs.length,
            searchCount,
            leadCount: leadByCity.get(city) || 0,
        })
    }

    return rows.sort((a, b) => b.totalBays - a.totalBays)
}

// ─── C. Pricing deep dive ───

export function computePriceBands(parkings: NormalisedParking[]): PriceBandRow[] {
    const cityMap = new Map<string, number[]>()
    for (const p of parkings) {
        if (p.monthlyPriceCents == null) continue
        if (!cityMap.has(p.metro)) cityMap.set(p.metro, new Array(PRICE_BANDS.length).fill(0))
        const bands = cityMap.get(p.metro)!
        for (let i = 0; i < PRICE_BANDS.length; i++) {
            if (p.monthlyPriceCents >= PRICE_BANDS[i].min && p.monthlyPriceCents < PRICE_BANDS[i].max) {
                bands[i]++
                break
            }
        }
    }

    return Array.from(cityMap.entries())
        .map(([city, bands]) => ({ city, bands }))
        .sort((a, b) => b.bands.reduce((s, v) => s + v, 0) - a.bands.reduce((s, v) => s + v, 0))
}

export function computeSuburbLeague(parkings: NormalisedParking[], topN: number = 15): { most: SuburbRow[]; least: SuburbRow[] } {
    const suburbMap = new Map<string, { prices: number[]; city: string; count: number; bays: number }>()

    for (const p of parkings) {
        if (p.monthlyPriceCents == null || !p.suburb) continue
        const key = `${p.suburb}|${p.metro}`
        const entry = suburbMap.get(key) || { prices: [], city: p.metro, count: 0, bays: 0 }
        entry.prices.push(p.monthlyPriceCents)
        entry.count++
        entry.bays += p.raw.numberOfBays || 0
        suburbMap.set(key, entry)
    }

    const rows: SuburbRow[] = Array.from(suburbMap.entries())
        .filter(([, v]) => v.count >= 2)
        .map(([key, v]) => ({
            suburb: key.split('|')[0],
            city: v.city,
            medianPrice: centsToRands(median(v.prices)),
            meanPrice: centsToRands(mean(v.prices)),
            bayCount: v.bays,
            parkingCount: v.count,
        }))

    const sorted = rows.sort((a, b) => b.medianPrice - a.medianPrice)
    return {
        most: sorted.slice(0, topN),
        least: sorted.slice(-topN).reverse(),
    }
}

export function computeAmenityPremiums(parkings: NormalisedParking[]): AmenityPremium[] {
    const amenities: { key: string; label: string; getter: (p: NormalisedParking) => boolean }[] = [
        { key: 'coveredParking', label: 'Covered Parking', getter: (p) => p.hasCoveredParking },
        { key: 'onSiteGuard', label: 'On-Site Guard', getter: (p) => p.hasOnSiteGuard },
        { key: 'twentyFourSevenAccess', label: '24/7 Access', getter: (p) => p.has247Access },
        { key: 'basementParking', label: 'Basement Parking', getter: (p) => p.hasBasementParking },
    ]

    return amenities.map(({ key, label, getter }) => {
        const withPrices = parkings.filter((p) => getter(p) && p.monthlyPriceCents != null).map((p) => p.monthlyPriceCents!)
        const withoutPrices = parkings.filter((p) => !getter(p) && p.monthlyPriceCents != null).map((p) => p.monthlyPriceCents!)

        const withAvg = centsToRands(mean(withPrices))
        const withoutAvg = centsToRands(mean(withoutPrices))

        return {
            amenity: key,
            label,
            withPrice: withAvg,
            withoutPrice: withoutAvg,
            premiumPct: withoutAvg > 0 ? ((withAvg - withoutAvg) / withoutAvg) * 100 : 0,
            withCount: withPrices.length,
            withoutCount: withoutPrices.length,
        }
    })
}

export function computeAskingVsAchieved(
    parkings: NormalisedParking[],
    subs: NormalisedSubscription[]
): AskingVsAchieved[] {
    const parkingPriceMap = new Map<string, number>()
    for (const p of parkings) {
        if (p.monthlyPriceCents != null && p.raw.$key) {
            parkingPriceMap.set(p.raw.$key, p.monthlyPriceCents)
        }
    }

    const cityData = new Map<string, { asking: number[]; achieved: number[] }>()

    for (const s of subs) {
        if (!s.isActive || !s.parkingKey || !s.metro) continue
        const askingPrice = parkingPriceMap.get(s.parkingKey)
        if (askingPrice == null) continue

        const entry = cityData.get(s.metro) || { asking: [], achieved: [] }
        entry.asking.push(askingPrice)
        entry.achieved.push(s.priceCents)
        cityData.set(s.metro, entry)
    }

    return Array.from(cityData.entries())
        .filter(([, v]) => v.asking.length >= 2)
        .map(([city, v]) => {
            const askingMed = centsToRands(median(v.asking))
            const achievedMed = centsToRands(median(v.achieved))
            return {
                city,
                askingMedian: askingMed,
                achievedMedian: achievedMed,
                discountPct: askingMed > 0 ? ((askingMed - achievedMed) / askingMed) * 100 : 0,
                sampleSize: v.asking.length,
            }
        })
        .sort((a, b) => b.sampleSize - a.sampleSize)
}

// ─── D. Demand & scarcity ───

export function computeLeadReasons(leads: NormalisedLead[]): LeadReasonRow[] {
    const cityMap = new Map<string, LeadReasonRow>()

    for (const l of leads) {
        const row = cityMap.get(l.metro) || {
            city: l.metro,
            tooFar: 0, tooExpensive: 0, requirementsNotMet: 0,
            fullyBooked: 0, other: 0, total: 0,
        }
        row.total++
        switch (l.reason) {
            case 'TOO_FAR_FROM_DESTINATION': row.tooFar++; break
            case 'TOO_EXPENSIVE': row.tooExpensive++; break
            case 'REQUIREMENTS_NOT_MET': row.requirementsNotMet++; break
            case 'FULLY_BOOKED': row.fullyBooked++; break
            default: row.other++; break
        }
        cityMap.set(l.metro, row)
    }

    return Array.from(cityMap.values()).sort((a, b) => b.total - a.total)
}

// ─── E. Uptake over time ───

export function computeMonthlyUptake(subs: NormalisedSubscription[]): MonthlyUptake[] {
    const buckets = new Map<string, MonthlyUptake>()

    for (const s of subs) {
        if (!s.createdAt) continue
        const d = s.createdAt
        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
        const label = d.toLocaleString('default', { month: 'short', year: 'numeric' })

        const bucket = buckets.get(key) || { month: key, label, newContracts: 0, baysContracted: 0, medianPrice: 0 }
        bucket.newContracts++
        bucket.baysContracted += s.baysBooked
        buckets.set(key, bucket)
    }

    const monthPrices = new Map<string, number[]>()
    for (const s of subs) {
        if (!s.createdAt || !s.priceCents) continue
        const key = `${s.createdAt.getFullYear()}-${String(s.createdAt.getMonth() + 1).padStart(2, '0')}`
        if (!monthPrices.has(key)) monthPrices.set(key, [])
        monthPrices.get(key)!.push(s.priceCents)
    }

    for (const [key, bucket] of buckets) {
        const prices = monthPrices.get(key) || []
        bucket.medianPrice = centsToRands(median(prices))
    }

    return Array.from(buckets.values()).sort((a, b) => a.month.localeCompare(b.month))
}

export function computeCityMonthlyUptake(subs: NormalisedSubscription[], topCities: string[]): CityMonthlyUptake[] {
    return topCities.map((city) => {
        const citySubs = subs.filter((s) => s.metro === city)
        return { city, months: computeMonthlyUptake(citySubs) }
    })
}

export function computeSeasonality(subs: NormalisedSubscription[]): { month: string; count: number }[] {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
    const counts = new Array(12).fill(0)

    for (const s of subs) {
        if (!s.createdAt) continue
        counts[s.createdAt.getMonth()]++
    }

    return months.map((m, i) => ({ month: m, count: counts[i] }))
}

// ─── F. Commitment & retention ───

export function computeTermMix(parkings: NormalisedParking[]): TermMixRow[] {
    const cityMap = new Map<string, TermMixRow>()

    for (const p of parkings) {
        const row = cityMap.get(p.metro) || { city: p.metro, fixedTerm: 0, monthToMonth: 0, allocated: 0, total: 0 }
        row.total++

        if (p.raw.parkingType === ParkingType.BOOKING) {
            row.allocated++
        } else if (p.raw.isMonthToMonth) {
            row.monthToMonth++
        } else {
            row.fixedTerm++
        }

        cityMap.set(p.metro, row)
    }

    return Array.from(cityMap.values()).sort((a, b) => b.total - a.total)
}

export function computeRenewalRates(subs: NormalisedSubscription[]): RenewalRow[] {
    const completedSubs = subs.filter((s) => s.raw.status === ContractStatus.completed || s.raw.status === ContractStatus.active)
    const cityMap = new Map<string, RenewalRow>()

    for (const s of completedSubs) {
        if (!s.metro) continue
        const row = cityMap.get(s.metro) || { city: s.metro, renewed: 0, notRenewed: 0, pending: 0, total: 0, renewalRate: 0 }
        row.total++

        const renewalStatus = s.raw.renewalStatus
        if (renewalStatus === SubscriptionRenewalStatus.renewedSameParking || renewalStatus === SubscriptionRenewalStatus.renewedOtherParking) {
            row.renewed++
        } else if (renewalStatus === SubscriptionRenewalStatus.notRenewed || renewalStatus === SubscriptionRenewalStatus.willNotRenew) {
            row.notRenewed++
        } else {
            row.pending++
        }

        cityMap.set(s.metro, row)
    }

    for (const row of cityMap.values()) {
        const decided = row.renewed + row.notRenewed
        row.renewalRate = decided > 0 ? (row.renewed / decided) * 100 : 0
    }

    return Array.from(cityMap.values()).sort((a, b) => b.total - a.total)
}

export function computeNonRenewalReasons(subs: NormalisedSubscription[]): NonRenewalReasonRow[] {
    const reasonLabels: Record<string, string> = {
        [NonRenewalReason.FOUND_ALTERNATIVE]: 'Found Alternative',
        [NonRenewalReason.TOO_EXPENSIVE]: 'Too Expensive',
        [NonRenewalReason.NO_LONGER_NEEDED]: 'No Longer Needed',
        [NonRenewalReason.RELOCATED]: 'Relocated',
        [NonRenewalReason.POOR_EXPERIENCE]: 'Poor Experience',
        [NonRenewalReason.OTHER]: 'Other',
    }

    const counts = new Map<string, number>()
    for (const s of subs) {
        const reason = s.raw.nonRenewalReason
        if (!reason) continue
        counts.set(reason, (counts.get(reason) || 0) + 1)
    }

    return Array.from(counts.entries())
        .map(([reason, count]) => ({
            reason,
            label: reasonLabels[reason] || reason,
            count,
        }))
        .sort((a, b) => b.count - a.count)
}

export function computeContractDurationDistribution(subs: NormalisedSubscription[]): { duration: string; count: number }[] {
    const buckets: Record<string, number> = {
        '1 month': 0,
        '2-3 months': 0,
        '4-6 months': 0,
        '7-12 months': 0,
        '13+ months': 0,
    }

    for (const s of subs) {
        if (s.period <= 1) buckets['1 month']++
        else if (s.period <= 3) buckets['2-3 months']++
        else if (s.period <= 6) buckets['4-6 months']++
        else if (s.period <= 12) buckets['7-12 months']++
        else buckets['13+ months']++
    }

    return Object.entries(buckets).map(([duration, count]) => ({ duration, count }))
}

// ─── Search volume over time ───

export function computeSearchVolumeByCity(searches: NormalisedSearch[]): { city: string; months: { month: string; label: string; count: number }[] }[] {
    const cityMonthMap = new Map<string, Map<string, { label: string; count: number }>>()

    for (const s of searches) {
        if (!s.createdAt) continue
        const d = s.createdAt
        const monthKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
        const label = d.toLocaleString('default', { month: 'short', year: 'numeric' })

        if (!cityMonthMap.has(s.metro)) cityMonthMap.set(s.metro, new Map())
        const monthMap = cityMonthMap.get(s.metro)!
        const entry = monthMap.get(monthKey) || { label, count: 0 }
        entry.count++
        monthMap.set(monthKey, entry)
    }

    return Array.from(cityMonthMap.entries())
        .map(([city, monthMap]) => ({
            city,
            months: Array.from(monthMap.entries())
                .map(([month, data]) => ({ month, ...data }))
                .sort((a, b) => a.month.localeCompare(b.month)),
        }))
        .sort((a, b) => {
            const totalA = a.months.reduce((s, m) => s + m.count, 0)
            const totalB = b.months.reduce((s, m) => s + m.count, 0)
            return totalB - totalA
        })
}
